import { useAppDispatch, useAppSelector } from '@/hooks';
import {
  useCompleteChecklistMutation,
  useReplaceChecklistMutation,
  useUpdateChecklistItemMutation,
} from '@/services/checklists';
import { useGetChecklistTemplatesQuery } from '@/services/checklistTemplates';
import { useUploadDocumentMutation } from '@/services/documents';
import { useAddFlagToQueueMutation, useGetQueueByIdQuery, useResolveFlagMutation } from '@/services/queues';
import {
  activateFlaggingMode,
  deactivateFlaggingMode,
  setActiveFlowIndex as syncFlaggingFlowIndex,
} from '@/store/reducers/flaggingModeSlice';
import { PalmettoIcon, PalmettoTextInput } from '@palmetto/dsm-components';
import { toast } from '@palmetto/palmetto-components';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { AllDocumentTypes, RichTextNode } from 'types';
import {
  type Clientize,
  type ChecklistDocumentView,
  type ChecklistItemInputDefinitionView,
  type ChecklistPendingFlagDecision,
  ChecklistItemStatus,
  type ChecklistTemplateSection,
  FlagDocumentTypes,
  type FlagPayload,
  type FlagPurposeType,
  FlagStatus,
  APPROVED_QUEUE_STATUSES,
  type QueueStatus,
} from 'types';
import type { EvaluationState } from 'types';

import { CompleteQueueReview } from '../Queues/CompleteQueueReview/CompleteQueueReview';

import { buildFlagProblems } from './buildFlagProblems';
import {
  ChecklistFlagCreationFlow,
  type ChecklistFlagFlowState,
  type ChecklistFlagWizardDraft,
} from './ChecklistFlagCreationFlow';
import { ChecklistItemCard, type ActiveItemFlag, type FlagDecision } from './ChecklistItemCard';
import { ChecklistProgress } from './ChecklistProgress';
import { itemMatchesSearch } from './checklistSearch';
import { StaleTemplateVersionBanner } from './StaleTemplateVersionBanner';

// A checklist item's `data` bag, widened to also hold pending (not-yet-finalized) flag
// decision drafts alongside terminal string|boolean values (see ChecklistPendingFlagDecision).
type ItemDataBag = Record<string, string | boolean | ChecklistPendingFlagDecision[]>;

// Removes a just-finalized flag's draft entry from `data[inputKey]` so a completed flag
// doesn't leave a stale duplicate draft behind. Only touches entries for flows that actually
// carry an option-level identity (`inputKey`+`optionValue`) — evaluation-auto-produced flows
// have no per-option draft slot to strip.
const stripFinalizedDraftEntries = (data: ItemDataBag | undefined, finalizedFlows: ActiveFlow[]): ItemDataBag => {
  const next: ItemDataBag = { ...data };
  for (const flow of finalizedFlows) {
    if (!flow.inputKey || !flow.optionValue) continue;
    const entries = next[flow.inputKey];
    if (!Array.isArray(entries)) continue;
    next[flow.inputKey] = entries.filter((entry) => entry.optionValue !== flow.optionValue);
  }
  return next;
};

const TABS = { needsReview: 'needsReview', reviewed: 'reviewed' } as const;
// Stable fallback so items with no active flags don't get a new object literal every render.
const EMPTY_ACTIVE_FLAGS_BY_INPUT_KEY: Record<string, ActiveItemFlag[]> = {};
type Tab = (typeof TABS)[keyof typeof TABS];

export interface ChecklistPanelProps {
  checklist: Clientize<ChecklistDocumentView>;
  onUpdateItem: (
    itemKey: string,
    status: ChecklistItemStatus,
    data: ItemDataBag,
    evaluations?: Record<string, EvaluationState>,
  ) => Promise<void>;
  updatingItemKey: string | null;
  queueId: string;
  onFlagFlowChange?: (active: boolean) => void;
  onCompleteReview?: (targetStatus?: QueueStatus) => Promise<void>;
}

interface SectionGroup {
  section: ChecklistTemplateSection | null;
  items: Clientize<ChecklistDocumentView>['items'];
}

interface ActiveFlow {
  itemKey: string;
  itemName: string;
  inputData: Record<string, string | boolean>;
  evaluations?: Record<string, EvaluationState>;
  preselectedRequirementId?: string;
  preselectedPurposeType?: FlagPurposeType;
  preselectedNote?: string;
  // Set for evaluation-produced flag decisions (identifies the evaluation input the
  // auto-raised flag came from).
  checklistInputKey?: string;
  // Set for option-toggle-produced flag decisions (FIN-4866 multi-flag support) —
  // identifies which input/option this flow came from so the resulting Flag can be
  // tagged with `checklistInputKey`/`checklistOptionValue`. Mutually exclusive with
  // `checklistInputKey` above (evaluation decisions never set these).
  inputKey?: string;
  optionValue?: string;
  allowedDocumentTypes?: AllDocumentTypes[];
}

interface ChecklistItemCardRowProps {
  itemKey: string;
  name: string;
  description: string;
  tooltipText?: RichTextNode[];
  status: ChecklistItemStatus;
  aiCompleted?: boolean;
  inputs?: ChecklistItemInputDefinitionView[];
  savedData?: ItemDataBag;
  isExpanded: boolean;
  flagCount?: number;
  onToggleExpand: (itemKey: string) => void;
  onApprove: (
    itemKey: string,
    data: Record<string, string | boolean>,
    evaluations: Record<string, EvaluationState>,
  ) => void;
  onStartFlagFlow: (
    itemKey: string,
    data: Record<string, string | boolean>,
    flagDecisions: FlagDecision[],
    evaluations: Record<string, EvaluationState>,
  ) => void;
  onUnreview: (itemKey: string) => void;
  isUpdating: boolean;
  isOptimisticInProgress?: boolean;
  activeFlagsByInputKey?: Record<string, ActiveItemFlag[]>;
  onRemoveFlag?: (flagId: string) => void;
  savedEvaluations?: Record<string, EvaluationState>;
  onPersistDraft?: (itemKey: string, data: ItemDataBag, evaluations?: Record<string, EvaluationState>) => void;
}

const ChecklistItemCardRow: React.FC<ChecklistItemCardRowProps> = ({
  itemKey,
  name,
  description,
  tooltipText,
  status,
  aiCompleted,
  inputs,
  savedData,
  isExpanded,
  flagCount,
  onToggleExpand,
  onApprove,
  onStartFlagFlow,
  onUnreview,
  isUpdating,
  isOptimisticInProgress,
  activeFlagsByInputKey,
  onRemoveFlag,
  savedEvaluations,
  onPersistDraft,
}) => (
  <ChecklistItemCard
    itemKey={itemKey}
    name={name}
    description={description}
    tooltipText={tooltipText}
    status={status}
    aiCompleted={aiCompleted}
    inputs={inputs}
    savedData={savedData}
    isExpanded={isExpanded}
    flagCount={flagCount}
    onToggleExpand={onToggleExpand}
    onApprove={onApprove}
    onStartFlagFlow={onStartFlagFlow}
    onUnreview={onUnreview}
    isUpdating={isUpdating}
    isOptimisticInProgress={isOptimisticInProgress}
    activeFlagsByInputKey={activeFlagsByInputKey}
    onRemoveFlag={onRemoveFlag}
    savedEvaluations={savedEvaluations}
    onPersistDraft={onPersistDraft}
  />
);

export const ChecklistPanel: React.FC<ChecklistPanelProps> = ({
  checklist,
  onUpdateItem,
  updatingItemKey,
  queueId,
  onFlagFlowChange,
  onCompleteReview,
}) => {
  const dispatch = useAppDispatch();
  const pendingAttachments = useAppSelector((state) => state.flaggingMode.pendingAttachments);

  const [activeTab, setActiveTab] = useState<Tab>(TABS.needsReview);
  const [expandedItemKey, setExpandedItemKey] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const isSearchActive = searchQuery.trim().length > 0;
  const [activeFlowQueue, setActiveFlowQueue] = useState<ActiveFlow[]>([]);
  const [activeFlowIndex, setActiveFlowIndex] = useState(0);
  const [savedFlowStates, setSavedFlowStates] = useState<Record<number, ChecklistFlagFlowState>>({});
  const [pendingItemData, setPendingItemData] = useState<Record<string, Record<string, string | boolean>>>({});
  const [pendingEvaluations, setPendingEvaluations] = useState<Record<string, Record<string, EvaluationState>>>({});
  const [isSavingFlags, setIsSavingFlags] = useState(false);
  const [flagSaveError, setFlagSaveError] = useState<string | null>(null);
  const [optimisticInProgressKeys, setOptimisticInProgressKeys] = useState<Set<string>>(new Set());
  // Item keys with a draft-only persist currently in flight — `onUpdateItem` is the same
  // shared channel authoritative saves use, and its caller (ChecklistDrawerContent) marks
  // `updatingItemKey` for ANY call, draft or authoritative. Without this, a background draft
  // save fired on blur/close (e.g. right after selecting a terminal option) would spuriously
  // disable Complete/Create flag while it's in flight, blocking the very next legitimate click.
  const [draftPersistingItemKeys, setDraftPersistingItemKeys] = useState<Set<string>>(new Set());
  // Item keys with a genuine authoritative save (Approve/Complete) currently in flight.
  // Tracked separately from the draft set above so the two can overlap: if a draft is still
  // resolving when Complete is clicked for the same item, `updatingItemKey` alone can't tell
  // the two apart, but this set can — the button must stay disabled whenever an authoritative
  // save is in flight, even if a draft-only save also happens to be in flight at the same time.
  const [authoritativeInFlightItemKeys, setAuthoritativeInFlightItemKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!isSearchActive) setExpandedItemKey(null);
  }, [isSearchActive]);

  const { data: queueData } = useGetQueueByIdQuery({ id: queueId });

  const { data: currentTemplateData } = useGetChecklistTemplatesQuery(
    { templateGroupId: checklist.templateGroupId, isCurrent: true, limit: 1 },
    { skip: !checklist.templateGroupId || checklist.templateVersion == null },
  );
  const currentTemplateVersion = currentTemplateData?.data?.[0]?.version;
  const hasNewerTemplateVersion =
    currentTemplateVersion != null &&
    checklist.templateVersion != null &&
    currentTemplateVersion > checklist.templateVersion;
  const hasNoHistory = checklist.history.length === 0;
  const canReplace = hasNewerTemplateVersion && hasNoHistory;

  const [addFlagToQueue] = useAddFlagToQueueMutation();
  const [resolveFlag] = useResolveFlagMutation();
  const [updateChecklistItem] = useUpdateChecklistItemMutation();
  const [uploadDocument] = useUploadDocumentMutation();
  const [completeChecklist, { isLoading: isCompletingChecklist }] = useCompleteChecklistMutation();
  const [replaceChecklist, { isLoading: isReplacing }] = useReplaceChecklistMutation();

  const sortedItems = useMemo(() => [...checklist.items].toSorted((a, b) => a.order - b.order), [checklist.items]);

  // `pendingItemData[itemKey]` is a terminal-values-only optimistic snapshot kept around
  // after a flag flow starts (see the comment at its clearing site below) — it must be
  // merged over `item.data`, not replace it outright, or a persisted ChecklistPendingFlagDecision[]
  // draft becomes invisible to rehydration the moment any flag flow has ever been opened for
  // that item, even a cancelled one.
  const savedDataByItemKey = useMemo(() => {
    const map: Record<string, ItemDataBag> = {};
    for (const item of checklist.items) {
      map[item.key] = { ...item.data, ...pendingItemData[item.key] };
    }
    return map;
  }, [checklist.items, pendingItemData]);

  const needsReviewItems = useMemo(
    () =>
      sortedItems.filter(
        (item) =>
          item.status === ChecklistItemStatus.pending ||
          item.status === ChecklistItemStatus.inProgress ||
          item.status === ChecklistItemStatus.skipped,
      ),
    [sortedItems],
  );

  const reviewedItems = useMemo(
    () => sortedItems.filter((item) => item.status === ChecklistItemStatus.completed),
    [sortedItems],
  );

  const activeTabItems = activeTab === TABS.needsReview ? needsReviewItems : reviewedItems;
  const displayedItems = isSearchActive
    ? activeTabItems.filter((item) => itemMatchesSearch(item, searchQuery))
    : activeTabItems;
  const reviewedCount = reviewedItems.length;
  const totalCount = checklist.items.length;

  const sections = useMemo(
    () => (checklist.sections ?? []).slice().sort((a, b) => a.order - b.order),
    [checklist.sections],
  );

  const flagCountByItemKey = useMemo(() => {
    const counts: Record<string, number> = {};
    if (!queueData?.flags) return counts;
    for (const flag of queueData.flags) {
      // A removed flag (closedDeleted, resolved: true) must stop counting toward the
      // item's flag badge the moment it's removed.
      if (flag.checklistItemKey && !flag.resolved) {
        counts[flag.checklistItemKey] = (counts[flag.checklistItemKey] ?? 0) + 1;
      }
    }
    return counts;
  }, [queueData?.flags]);

  // Currently-open flags for requirement inputs, grouped by item key then input key, so
  // each ChecklistItemCard can render its own inputs' persisted flag chips. `value` is an
  // exact match against the backing ChecklistItemInputOption's `value` (read directly from
  // `flag.checklistOptionValue`) — not derived from `purposeType`.
  const activeFlagsByItemAndInputKey = useMemo(() => {
    const groups: Record<string, Record<string, ActiveItemFlag[]>> = {};
    if (!queueData?.flags) return groups;
    for (const flag of queueData.flags) {
      if (flag.resolved || !flag.checklistItemKey || !flag.checklistInputKey || !flag.checklistOptionValue) continue;
      if (!flag.id) continue;
      const itemGroup = (groups[flag.checklistItemKey] ??= {});
      const inputGroup = (itemGroup[flag.checklistInputKey] ??= []);
      inputGroup.push({ flagId: flag.id.toString(), value: flag.checklistOptionValue });
    }
    return groups;
  }, [queueData?.flags]);

  const sectionGroups = useMemo((): SectionGroup[] => {
    if (sections.length === 0) {
      return [{ section: null, items: displayedItems }];
    }

    const sectionMap = new Map<string, ChecklistTemplateSection>(sections.map((s) => [s.key, s]));
    const grouped = new Map<string | null, Clientize<ChecklistDocumentView>['items']>();

    for (const item of displayedItems) {
      const key = item.sectionKey && sectionMap.has(item.sectionKey) ? item.sectionKey : null;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(item);
    }

    const result: SectionGroup[] = [];

    const ungrouped = grouped.get(null);
    if (ungrouped && ungrouped.length > 0) {
      result.push({ section: null, items: ungrouped });
    }

    for (const section of sections) {
      const items = grouped.get(section.key) ?? [];
      if (items.length > 0) {
        result.push({ section, items });
      }
    }

    return result;
  }, [sections, displayedItems]);

  const handleToggleExpand = useCallback((itemKey: string) => {
    setExpandedItemKey((prev) => (prev === itemKey ? null : itemKey));
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  // Adds/removes `itemKey` from the given in-flight tracking set around an `onUpdateItem`
  // call. `Promise.resolve(...)` tolerates a test mock that doesn't return a real promise;
  // `onUpdateItem`'s own implementation (ChecklistDrawerContent) already catches and toasts
  // on failure rather than rejecting, so this never needs its own `.catch()`.
  const trackItemKeyInFlight = useCallback(
    (
      setInFlightKeys: React.Dispatch<React.SetStateAction<Set<string>>>,
      itemKey: string,
      call: Promise<void> | void,
    ) => {
      setInFlightKeys((prev) => (prev.has(itemKey) ? prev : new Set(prev).add(itemKey)));
      void Promise.resolve(call).finally(() => {
        setInFlightKeys((prev) => {
          if (!prev.has(itemKey)) return prev;
          const next = new Set(prev);
          next.delete(itemKey);
          return next;
        });
      });
    },
    [],
  );

  const handleApprove = useCallback(
    (itemKey: string, data: Record<string, string | boolean>, evaluations: Record<string, EvaluationState>) => {
      trackItemKeyInFlight(
        setAuthoritativeInFlightItemKeys,
        itemKey,
        onUpdateItem(itemKey, ChecklistItemStatus.completed, data, evaluations),
      );
    },
    [onUpdateItem, trackItemKeyInFlight],
  );

  const handleUnreview = useCallback(
    async (itemKey: string) => {
      await onUpdateItem(itemKey, ChecklistItemStatus.pending, {});
      setOptimisticInProgressKeys((prev) => {
        const next = new Set(prev);
        next.delete(itemKey);
        return next;
      });
    },
    [onUpdateItem],
  );

  // Persists a pending draft — a terminal decision, a flag-producing selection, or
  // evaluation state — the moment it's settled (blurred/closed), well before "Complete"/
  // "Save Flag(s)" is clicked. Same field/PATCH the approved path already uses; the item's
  // current status is left unchanged (a draft never completes the item). `evaluations`
  // passes straight through to `onUpdateItem`'s existing 4th parameter.
  const handlePersistDraft = useCallback(
    (itemKey: string, data: ItemDataBag, evaluations?: Record<string, EvaluationState>) => {
      const item = checklist.items.find((i) => i.key === itemKey);
      if (!item) return;
      trackItemKeyInFlight(
        setDraftPersistingItemKeys,
        itemKey,
        onUpdateItem(itemKey, item.status, data, evaluations),
      );
    },
    [checklist.items, onUpdateItem, trackItemKeyInFlight],
  );

  // Merges a debounced wizard-field edit (note/reason/criterion/problems) into the matching
  // ChecklistPendingFlagDecision entry for that option and persists it — `flowIndex` is
  // resolved against `activeFlowQueue`, not the currently-active flow, since the edit may
  // have been captured before the reviewer navigated to a different flag in the queue.
  const handleDraftChange = useCallback(
    (flowIndex: number, draft: ChecklistFlagWizardDraft) => {
      const flow = activeFlowQueue[flowIndex];
      if (!flow || !flow.inputKey || !flow.optionValue) return;
      const item = checklist.items.find((i) => i.key === flow.itemKey);
      if (!item) return;

      const itemData = item.data;
      const existingEntries = Array.isArray(itemData?.[flow.inputKey])
        ? (itemData[flow.inputKey] as ChecklistPendingFlagDecision[])
        : [];
      const matchIndex = existingEntries.findIndex((entry) => entry.optionValue === flow.optionValue);
      const base: ChecklistPendingFlagDecision =
        matchIndex >= 0
          ? existingEntries[matchIndex]
          : {
              optionValue: flow.optionValue,
              purposeType: flow.preselectedPurposeType as FlagPurposeType,
              requirementFlagSettingId: flow.preselectedRequirementId,
            };

      const merged: ChecklistPendingFlagDecision = {
        ...base,
        note: draft.note || undefined,
        selectedReason: draft.selectedReason ?? undefined,
        selectedCriterionId: draft.selectedCriterionId ?? undefined,
        selectedProblems: draft.selectedProblems.length > 0 ? draft.selectedProblems : undefined,
      };

      const nextEntries =
        matchIndex >= 0
          ? existingEntries.map((entry, idx) => (idx === matchIndex ? merged : entry))
          : [...existingEntries, merged];

      void onUpdateItem(flow.itemKey, item.status, { ...itemData, [flow.inputKey]: nextEntries });
    },
    [activeFlowQueue, checklist.items, onUpdateItem],
  );

  const handleCompleteChecklist = useCallback(async () => {
    try {
      await completeChecklist({ checklistId: checklist.id }).unwrap();
    } catch {
      toast.error('Failed to complete checklist review');
    }
  }, [checklist.id, completeChecklist]);

  const handleReplaceChecklist = useCallback(async () => {
    try {
      await replaceChecklist({ checklistId: checklist.id, queueId: checklist.queueId }).unwrap();
    } catch {
      toast.error('Failed to start over with the latest template');
    }
  }, [checklist.id, checklist.queueId, replaceChecklist]);

  const handleStartFlagFlow = useCallback(
    (
      itemKey: string,
      inputData: Record<string, string | boolean>,
      flagDecisions: FlagDecision[],
      evaluations: Record<string, EvaluationState>,
    ) => {
      const item = checklist.items.find((i) => i.key === itemKey);
      if (!item) return;
      setPendingItemData((prev) => ({ ...prev, [itemKey]: inputData }));
      setPendingEvaluations((prev) => ({ ...prev, [itemKey]: evaluations }));

      const template = currentTemplateData?.data?.[0];
      const templateItems = [
        ...(template?.items ?? []),
        ...(template?.sections ?? []).flatMap((s: any) => s.items ?? []),
      ];
      const templateItem = templateItems.find((ti: any) => ti.key === itemKey);
      const allowedDocumentTypes = item.documentTypes?.length
        ? item.documentTypes
        : (templateItem?.documentTypes ?? []);
      const baseFlow: ActiveFlow = {
        itemKey,
        itemName: item.name,
        inputData,
        evaluations,
        allowedDocumentTypes,
      };

      const queue: ActiveFlow[] =
        flagDecisions.length > 0
          ? flagDecisions.map(
              ({
                purposeType,
                label,
                requirementFlagSettingId,
                checklistInputKey,
                note,
                inputKey,
                optionValue,
              }) => ({
                ...baseFlow,
                itemName: label,
                preselectedPurposeType: purposeType,
                ...(requirementFlagSettingId && { preselectedRequirementId: requirementFlagSettingId }),
                ...(checklistInputKey && { checklistInputKey }),
                ...(note && { preselectedNote: note }),
                ...(inputKey && { inputKey }),
                ...(optionValue && { optionValue }),
              }),
            )
          : [baseFlow];

      // Pre-fill from any persisted ChecklistPendingFlagDecision draft, matched by
      // optionValue on `item.data[inputKey]` — alongside the preselectedPurposeType/
      // preselectedRequirementId wiring above for the option-level fields. This is what
      // makes a note typed mid-wizard survive a navigate-away-and-back on reopen.
      const itemData = item.data;
      const initialSavedFlowStates: Record<number, ChecklistFlagFlowState> = {};
      queue.forEach((flow, index) => {
        if (!flow.inputKey || !flow.optionValue) return;
        const entries = itemData?.[flow.inputKey];
        if (!Array.isArray(entries)) return;
        const persisted = entries.find((entry) => entry.optionValue === flow.optionValue);
        if (!persisted) return;
        initialSavedFlowStates[index] = {
          note: persisted.note ?? '',
          selectedFiles: [],
          selectedCriterionId: persisted.selectedCriterionId ?? null,
          selectedProblems: persisted.selectedProblems ?? [],
          selectedReason: persisted.selectedReason ?? null,
          localRequirementId: undefined,
          flagType: undefined,
          dataKey: undefined,
        };
      });

      setActiveFlowQueue(queue);
      setActiveFlowIndex(0);
      setSavedFlowStates(initialSavedFlowStates);
      setFlagSaveError(null);
      dispatch(
        activateFlaggingMode({
          flagLabel: item.name,
          allowedDocumentTypes,
          hasDecisionRendered: APPROVED_QUEUE_STATUSES.has(queueData?.status as QueueStatus),
        }),
      );
      onFlagFlowChange?.(true);
    },
    [checklist.items, currentTemplateData, dispatch, onFlagFlowChange],
  );

  const handleNextFlag = useCallback(
    (state: ChecklistFlagFlowState) => {
      setSavedFlowStates((prev) => ({ ...prev, [activeFlowIndex]: state }));
      setActiveFlowIndex((prev) => prev + 1);
      dispatch(syncFlaggingFlowIndex(activeFlowIndex + 1));
    },
    [activeFlowIndex, dispatch],
  );

  const handleBackFlag = useCallback(
    (state: ChecklistFlagFlowState) => {
      setSavedFlowStates((prev) => ({ ...prev, [activeFlowIndex]: state }));
      setActiveFlowIndex((prev) => prev - 1);
      dispatch(syncFlaggingFlowIndex(activeFlowIndex - 1));
    },
    [activeFlowIndex, dispatch],
  );

  const handleSaveAllFlags = useCallback(
    async (lastState: ChecklistFlagFlowState) => {
      setIsSavingFlags(true);
      setFlagSaveError(null);

      const allStates: ChecklistFlagFlowState[] = activeFlowQueue.map((_, i) =>
        i === activeFlowIndex ? lastState : savedFlowStates[i],
      );

      // Only the flows that actually succeed get their draft stripped — on a partial
      // failure, the still-unsaved flow's draft must survive so it isn't lost on retry.
      const succeededFlows: ActiveFlow[] = [];

      try {
        for (let i = 0; i < activeFlowQueue.length; i++) {
          const flow = activeFlowQueue[i];
          const state = allStates[i];

          let fileIds: string[] | undefined;
          if (state.selectedFiles.length > 0) {
            const formData = new FormData();
            formData.append('type', FlagDocumentTypes.flag);
            state.selectedFiles.forEach((file, idx) => {
              formData.append(`files[${idx}]`, file);
            });
            const { documents } = await uploadDocument({
              accountId: checklist.accountId,
              formData,
              invalidateAllTags: false,
            }).unwrap();
            fileIds = documents.map((d) => d.id?.toString()).filter((id): id is string => !!id);
          }

          const problems =
            state.selectedCriterionId && state.selectedProblems.length > 0
              ? buildFlagProblems(state.selectedProblems, state.selectedCriterionId)
              : undefined;

          // Default the note to the pre-filled evaluation reason when the tech left it blank.
          const note = state.note || flow.preselectedNote;
          const flowAttachments = pendingAttachments[i] ?? [];

          const flag: FlagPayload = {
            ...(flow.preselectedPurposeType && { purposeType: flow.preselectedPurposeType }),
            ...(state.dataKey && { dataKey: state.dataKey }),
            ...(note && { note }),
            ...(state.selectedReason && { reason: state.selectedReason }),
            ...(problems && { problems }),
            ...(state.flagType && { flagType: state.flagType }),
            // fileUpload (single string) is what the flag viewer reads; all files land in one
            // document since FlagDocumentTypes.flag is not in SeparateDocumentTypes
            ...(fileIds && fileIds.length > 0 ? { fileUpload: fileIds[0] } : {}),
            ...(flowAttachments.length > 0 && { attachedDocumentIds: flowAttachments.map((a) => a.id) }),
            checklistItemKey: flow.itemKey,
            // checklistInputKey comes from either the evaluation path (flow.checklistInputKey)
            // or the option-toggle path (flow.inputKey) — the two are mutually exclusive per flow.
            ...((flow.checklistInputKey || flow.inputKey) && {
              checklistInputKey: flow.checklistInputKey ?? flow.inputKey,
            }),
            ...(flow.optionValue && { checklistOptionValue: flow.optionValue }),
          };

          await addFlagToQueue({ id: queueId, flag }).unwrap();
          succeededFlows.push(flow);
        }

        const itemKey = activeFlowQueue[0].itemKey;
        const flowEvaluations = activeFlowQueue[0].evaluations;
        const item = checklist.items.find((i) => i.key === itemKey);
        const strippedData = stripFinalizedDraftEntries(item?.data, succeededFlows);
        await updateChecklistItem({
          checklistId: checklist.id,
          itemKey,
          status: ChecklistItemStatus.inProgress,
          // Strip the now-finalized draft entries so a completed flag doesn't leave a stale
          // duplicate behind, alongside whatever terminal values the wizard step carried.
          data: { ...strippedData, ...activeFlowQueue[0].inputData },
          ...(flowEvaluations && Object.keys(flowEvaluations).length > 0 && { evaluations: flowEvaluations }),
        }).unwrap();

        // Keep pendingItemData so the card initializes with the correct decisions when it
        // remounts after the flow closes — the RTK re-fetch may not have completed yet.
        setOptimisticInProgressKeys((prev) => new Set([...prev, itemKey]));
        setActiveFlowQueue([]);
        setActiveFlowIndex(0);
        setSavedFlowStates({});
        setIsSavingFlags(false);
        dispatch(deactivateFlaggingMode());
        onFlagFlowChange?.(false);
      } catch {
        if (succeededFlows.length > 0) {
          const itemKey = activeFlowQueue[0].itemKey;
          const item = checklist.items.find((i) => i.key === itemKey);
          const strippedData = stripFinalizedDraftEntries(item?.data, succeededFlows);
          try {
            await updateChecklistItem({
              checklistId: checklist.id,
              itemKey,
              status: ChecklistItemStatus.inProgress,
              data: strippedData,
            }).unwrap();
          } catch {
            // Best-effort — if this also fails, the drafts for the already-created flags
            // remain until the next successful save or a fresh load.
          }
        }
        setIsSavingFlags(false);
        setFlagSaveError('Failed to save flags. Please try again.');
        toast.error('Failed to save flags. Please try again.');
      }
    },
    [
      activeFlowQueue,
      activeFlowIndex,
      savedFlowStates,
      queueId,
      checklist.id,
      checklist.items,
      checklist.accountId,
      addFlagToQueue,
      updateChecklistItem,
      uploadDocument,
      pendingAttachments,
      dispatch,
      onFlagFlowChange,
    ],
  );

  const handleCloseFlagFlow = useCallback(() => {
    setActiveFlowQueue([]);
    setActiveFlowIndex(0);
    setSavedFlowStates({});
    setIsSavingFlags(false);
    setFlagSaveError(null);
    dispatch(deactivateFlaggingMode());
    onFlagFlowChange?.(false);
  }, [dispatch, onFlagFlowChange]);

  // Removing a persisted flag chip is immediate — no wizard/note required — since the
  // flag was already fully specified when it was created.
  const handleRemoveFlag = useCallback(
    async (flagId: string) => {
      try {
        await resolveFlag({ id: queueId, dataKey: flagId, flagId, status: FlagStatus.closedDeleted }).unwrap();
      } catch {
        toast.error('Failed to remove flag. Please try again.');
      }
    },
    [queueId, resolveFlag],
  );

  const handleTabClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setActiveTab(event.currentTarget.value as Tab);
  }, []);

  const activeFlow = activeFlowQueue[activeFlowIndex] ?? null;

  if (activeFlow) {
    return (
      <ChecklistFlagCreationFlow
        key={`${activeFlow.itemKey}-${activeFlowIndex}`}
        queueId={queueId}
        itemName={activeFlow.itemName}
        preselectedRequirementId={activeFlow.preselectedRequirementId}
        preselectedPurposeType={activeFlow.preselectedPurposeType}
        preselectedNote={activeFlow.preselectedNote}
        currentIndex={activeFlowIndex}
        totalCount={activeFlowQueue.length}
        savedState={savedFlowStates[activeFlowIndex]}
        isSaving={isSavingFlags}
        saveError={flagSaveError}
        allowedDocumentTypes={activeFlow.allowedDocumentTypes}
        onNext={handleNextFlag}
        onBack={handleBackFlag}
        onComplete={handleSaveAllFlags}
        onCancel={handleCloseFlagFlow}
        onDraftChange={handleDraftChange}
      />
    );
  }

  return (
    <div className="flex flex-col gap-32 bg-canvas-default">
      {checklist.templateName && (
        <div className="flex flex-col gap-4">
          <h2 className="heading-400 text-text-primary">{checklist.templateName}</h2>
          {checklist.templateVersion != null && (
            <span className="body-300 text-text-tertiary">Version {checklist.templateVersion}</span>
          )}
        </div>
      )}
      <StaleTemplateVersionBanner
        hasNewerTemplateVersion={hasNewerTemplateVersion}
        latestTemplateVersion={currentTemplateVersion}
        canReplace={canReplace}
        isReplacing={isReplacing}
        onReplace={handleReplaceChecklist}
      />
      <ChecklistProgress reviewed={reviewedCount} total={totalCount} />

      <div className="flex flex-col gap-12">
        <PalmettoTextInput
          id="checklist-search"
          label="Search checklist"
          placeholder="Search checklist"
          value={searchQuery}
          onChange={handleSearchChange}
          leadingIcon={<PalmettoIcon name="searchMagnifyingGlass" size="xs" />}
          trailingIcon={
            isSearchActive ? (
              <button type="button" aria-label="Clear search" onClick={handleClearSearch}>
                <PalmettoIcon name="x" size="xs" />
              </button>
            ) : undefined
          }
        />
        <div className="p-1 gap-1 flex rounded-6 bg-canvas-bold" role="tablist">
          <button
            role="tab"
            aria-label="Needs review"
            aria-selected={activeTab === TABS.needsReview}
            type="button"
            className={`flex-1 cursor-pointer rounded-6 px-10 py-6 text-center body-400 transition-colors ${
              activeTab === TABS.needsReview
                ? 'bg-notification-canvas-default font-bold text-notification-content-on-default-primary'
                : 'text-text-primary'
            }`}
            value={TABS.needsReview}
            onClick={handleTabClick}
          >
            Needs review &middot; {needsReviewItems.length}
          </button>
          <button
            role="tab"
            aria-label="Reviewed"
            aria-selected={activeTab === TABS.reviewed}
            type="button"
            className={`flex-1 cursor-pointer rounded-6 px-10 py-6 text-center body-400 transition-colors ${
              activeTab === TABS.reviewed
                ? 'bg-notification-canvas-default font-bold text-notification-content-on-default-primary'
                : 'text-text-primary'
            }`}
            value={TABS.reviewed}
            onClick={handleTabClick}
          >
            Reviewed &middot; {reviewedItems.length}
          </button>
        </div>

        <div className="flex flex-col gap-8" role="tabpanel">
          {sectionGroups.map((group) => (
            <div key={group.section?.key ?? '__ungrouped__'} className="flex flex-col gap-12">
              {group.section && (
                <div className="flex h-24 items-center gap-10 pt-12 label-200 text-text-tertiary uppercase">
                  {group.section.title}
                </div>
              )}
              <div className="flex flex-col gap-8">
                {group.items.map((item) => (
                  <ChecklistItemCardRow
                    key={item.key}
                    itemKey={item.key}
                    name={item.name}
                    description={item.description}
                    tooltipText={item.tooltipText}
                    status={item.status}
                    aiCompleted={item.aiCompleted}
                    inputs={item.inputs as ChecklistItemInputDefinitionView[] | undefined}
                    savedData={savedDataByItemKey[item.key]}
                    isExpanded={isSearchActive || expandedItemKey === item.key}
                    flagCount={flagCountByItemKey[item.key] ?? 0}
                    onToggleExpand={handleToggleExpand}
                    onApprove={handleApprove}
                    onStartFlagFlow={handleStartFlagFlow}
                    onUnreview={handleUnreview}
                    isUpdating={
                      updatingItemKey === item.key &&
                      // A draft-only in-flight save must not disable the button, but a
                      // genuinely-concurrent authoritative save (or an in-flight call this
                      // component isn't tracking at all) must always keep it disabled.
                      (authoritativeInFlightItemKeys.has(item.key) || !draftPersistingItemKeys.has(item.key))
                    }
                    isOptimisticInProgress={optimisticInProgressKeys.has(item.key)}
                    activeFlagsByInputKey={activeFlagsByItemAndInputKey[item.key] ?? EMPTY_ACTIVE_FLAGS_BY_INPUT_KEY}
                    onRemoveFlag={handleRemoveFlag}
                    savedEvaluations={pendingEvaluations[item.key]}
                    onPersistDraft={handlePersistDraft}
                  />
                ))}
              </div>
            </div>
          ))}
          {displayedItems.length === 0 && (
            <p className="py-4 text-center body-300 text-text-secondary">
              {isSearchActive
                ? 'No items match your search.'
                : activeTab === TABS.needsReview
                  ? 'All items reviewed!'
                  : 'No reviewed items yet.'}
            </p>
          )}
        </div>
      </div>

      {needsReviewItems.length === 0 && totalCount > 0 && (
        <div className="flex flex-col gap-8">
          {checklist.completedBy ? (
            <p className="text-center body-200 text-text-secondary">Completed by {checklist.completedBy}</p>
          ) : (
            <button
              type="button"
              disabled={isCompletingChecklist}
              onClick={handleCompleteChecklist}
              className="flex w-full items-center justify-center gap-8 rounded-16 bg-canvas-success px-20 py-16 font-bold disabled:opacity-50"
            >
              <span className="text-text-on-inverted-primary">Complete Review</span>
            </button>
          )}
          {onCompleteReview && <CompleteQueueReview canSubmitReview={true} handleFormSave={onCompleteReview} />}
        </div>
      )}
    </div>
  );
};
